import { useParams, Link } from "react-router-dom";
import { CalendarDays, Gamepad2, Map, Users, Award, Layers, Users2 } from "lucide-react";
import { useFetch } from "@/lib/api";
import { usePageMeta, Loader, ErrorState, StatusBadge, BackLink, EmptyState } from "@/components/common";
import { LiveStandings } from "@/components/LiveStandings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtDateTime, PTYPE } from "@/lib/format";

const Info = ({ icon: Icon, label, value }) => (
  <div className="nx-card p-4">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#C6C6CE]/50"><Icon className="w-3.5 h-3.5 text-[#962FFF]" />{label}</div>
    <div className="mt-1.5 text-sm text-[#F5F5F7]">{value || "—"}</div>
  </div>
);

const tabCls = "data-[state=active]:bg-[#17171D] data-[state=active]:text-[#F5F5F7] text-[#C6C6CE]/70 font-display uppercase tracking-wider text-xs";

export default function EventDetail() {
  const { slug } = useParams();
  const { data, loading, error, reload } = useFetch(`/events/${slug}`);
  usePageMeta(data?.event?.title, data?.event?.description?.slice(0, 150));
  if (loading && !data) return <div className="pt-24"><Loader /></div>;
  if (error) return <div className="max-w-3xl mx-auto px-5 pt-32"><ErrorState message={error} onRetry={reload} /></div>;
  const { event: ev, registrations, regulation, final } = data;
  const rules = ev.score_rules || {};
  return (
    <div data-testid="event-detail-page">
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0"><img src={ev.poster_url || "/images/texture.jpg"} alt="" className="w-full h-full object-cover opacity-25" /><div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]/50" /></div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20 fade-up">
          <BackLink to="/eventi" label="Eventi" />
          <div className="mt-6 flex flex-wrap items-center gap-3"><StatusBadge status={ev.status} />{final && <Link to={`/risultati/${ev.slug}`} data-testid="event-official-results-link" className="text-xs text-[#d6b3ff] hover:underline">Risultati ufficiali disponibili</Link>}</div>
          <h1 data-testid="event-title" className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight silver-text max-w-4xl">{ev.title}</h1>
          <p className="mt-5 max-w-3xl prose-nx">{ev.description}</p>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          <Info icon={CalendarDays} label="Inizio" value={fmtDateTime(ev.start_at)} />
          <Info icon={CalendarDays} label="Conclusione" value={fmtDateTime(ev.end_at)} />
          <Info icon={Gamepad2} label="Modalità" value={ev.mode} />
          <Info icon={Map} label="Mappa" value={ev.map} />
          <Info icon={Layers} label="Formato" value={ev.format} />
          <Info icon={Users2} label="Partecipazione" value={PTYPE[ev.participant_type]} />
          <Info icon={Users} label="Partecipanti" value={`${registrations.length}${ev.max_participants ? ` / ${ev.max_participants}` : ""}`} />
          <Info icon={Award} label="Premi" value={ev.prizes ? "Vedi sezione premi" : "—"} />
        </div>
        <Tabs defaultValue="classifica">
          <TabsList className="bg-[#0B0B0F] border border-[#C6C6CE]/10 h-auto flex-wrap justify-start">
            <TabsTrigger value="classifica" data-testid="tab-standings" className={tabCls}>Classifica</TabsTrigger>
            <TabsTrigger value="partecipanti" data-testid="tab-participants" className={tabCls}>Partecipanti</TabsTrigger>
            <TabsTrigger value="regolamento" data-testid="tab-rules" className={tabCls}>Regolamento</TabsTrigger>
            <TabsTrigger value="premi" data-testid="tab-prizes" className={tabCls}>Premi</TabsTrigger>
          </TabsList>
          <TabsContent value="classifica" className="mt-6"><LiveStandings slug={ev.slug} /></TabsContent>
          <TabsContent value="partecipanti" className="mt-6">
            {registrations.length ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="participants-list">
                {registrations.map((r) => (
                  <div key={r.id} className="nx-card p-4">
                    <div className="flex items-center gap-2">
                      {r.tag && <span className="font-num text-[10px] px-1.5 py-0.5 border border-[#C6C6CE]/20 rounded">{r.tag}</span>}
                      {r.clan_slug ? <Link to={`/clan/${r.clan_slug}`} className="font-semibold text-[#F5F5F7] hover:text-[#962FFF]">{r.name}</Link> : <span className="font-semibold text-[#F5F5F7]">{r.name}</span>}
                    </div>
                    {r.players?.length > 0 && <div className="mt-2 text-xs text-[#C6C6CE]/60">{r.players.join(" · ")}</div>}
                  </div>
                ))}
              </div>
            ) : <EmptyState icon={Users} title="Nessun partecipante iscritto" />}
          </TabsContent>
          <TabsContent value="regolamento" className="mt-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2 nx-card p-6 md:p-8">
                <div className="eyebrow mb-3">Regolamento evento</div>
                <p className="prose-nx" data-testid="event-rules">{ev.rules || "Regolamento specifico non ancora pubblicato."}</p>
                {regulation && <Link to={`/regolamenti#${regulation.slug}`} className="mt-6 inline-block text-sm text-[#962FFF] hover:underline">Regolamento collegato: {regulation.title} (v{regulation.version})</Link>}
              </div>
              <div className="nx-card p-6" data-testid="score-rules">
                <div className="eyebrow mb-4">Sistema di punteggio</div>
                <ul className="space-y-2 text-sm text-[#C6C6CE]">
                  <li>Eliminazione: <span className="font-num text-[#F5F5F7]">{rules.kill_points} pt</span></li>
                  <li>Vittoria: <span className="font-num text-[#F5F5F7]">{rules.win_points} pt</span></li>
                  {(rules.extra_stats || []).map((s) => <li key={s.key}>{s.label}: <span className="font-num text-[#F5F5F7]">{s.points} pt</span></li>)}
                  <li>Piazzamento: <span className="font-num text-[#F5F5F7]">{(rules.placement_points || []).map((p, i) => `${i + 1}°=${p}`).join(", ") || "—"}</span></li>
                </ul>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="premi" className="mt-6">
            <div className="nx-card p-6 md:p-8"><p className="prose-nx" data-testid="event-prizes">{ev.prizes || "Premi non ancora comunicati."}</p></div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
